import { gql, useMutation } from '@apollo/client'
import { useToast } from '@farol-ds/react'
import { useCallback, useContext, useEffect, useRef } from 'react'

import { useSendOneClickButtonEvents } from '../../hooks'
import { BoxLockContext } from '../context'
import { SubscribeButton, SubscribeButtonProps } from '../subscribe-button'
import {
  OneClickButton_OfferLockCardFragment,
  OneClickButton_ViewerFragment,
  OneClickBuyMutation,
  OneClickBuyMutationVariables,
} from './__generated__/one-click-button.graphql'
import { useOneClickSuccessExperiment } from './use-one-click-success-experiment'
import { useWaitSubscriptionUpdate } from './use-wait-subscription-update'

type OneClickButtonProps = {
  offerLockCard: OneClickButton_OfferLockCardFragment
  viewer: OneClickButton_ViewerFragment
  kind?: SubscribeButtonProps['kind']
  leftIcon?: SubscribeButtonProps['leftIcon']
  forceDisabled?: boolean
  customLabel?: string
  onClick?: () => void
}

export const ONE_CLICK_BUY_MUTATION = gql`
  mutation oneClickBuy($input: OneClickBuyInput!) {
    oneClickBuy(input: $input) {
      orderCode
      success
    }
  }
`

const ONE_CLICK_CHECKOUT_FALLBACK_VARIANTS = ['checkout', 'embedded-checkout'] as const
type OneClickCheckoutFallbackVariant = (typeof ONE_CLICK_CHECKOUT_FALLBACK_VARIANTS)[number]

const isCheckoutFallbackVariant = (
  variant: string | null
): variant is OneClickCheckoutFallbackVariant =>
  ONE_CLICK_CHECKOUT_FALLBACK_VARIANTS.some((v) => v === variant)

export function OneClickButton(props: OneClickButtonProps) {
  const {
    offerLockCard,
    viewer,
    kind,
    leftIcon,
    forceDisabled,
    customLabel,
    onClick: onClickProp,
  } = props
  const toast = useToast()

  const context = useContext(BoxLockContext)
  const { open, interactive, setStatus, enableSuccessScreen, prefetch } = context

  // Only fetch experiment if this is a modal component and modal is open or prefetching
  const shouldFetchExperiment = context.componentType === 'modal' && (open || prefetch)

  // Get setEnableSuccessScreen from context (will be undefined for paywall)
  const setEnableSuccessScreen =
    'setEnableSuccessScreen' in context ? context.setEnableSuccessScreen : undefined

  const handleExperimentResult = useCallback(
    (enabled: boolean) => {
      if (setEnableSuccessScreen) {
        setEnableSuccessScreen(enabled)
      }
    },
    [setEnableSuccessScreen]
  )

  useOneClickSuccessExperiment({
    shouldFetch: shouldFetchExperiment,
    onResult: handleExperimentResult,
  })

  const { waitSubscriptionUpdate } = useWaitSubscriptionUpdate({ viewer })
  const [oneClickBuy] = useMutation<OneClickBuyMutation, OneClickBuyMutationVariables>(
    ONE_CLICK_BUY_MUTATION
  )
  const {
    sendFailureEvent,
    sendSuccessEvent,
    sendViewEvent,
    sendSubmittedEvent,
    sendCheckoutFallbackOpenedEvent,
    metricsRef,
  } = useSendOneClickButtonEvents({
    offerLockCard,
    viewer,
  })

  const sendViewEventRef = useRef(sendViewEvent)
  sendViewEventRef.current = sendViewEvent

  useEffect(() => {
    if (open) {
      sendViewEventRef.current()
    }
  }, [open])

  const { submitData, checkoutFallbackVariant } = offerLockCard
  const ccToken = offerLockCard.creditCard?.ccToken
  if (!ccToken) {
    return null
  }

  const showToast = (status: 'success' | 'error') => {
    const toastMessage = {
      success: 'Plano assinado com sucesso. Os novos recursos já estão disponíveis.',
      error: 'Erro ao processar pagamento. Tente novamente ou atualize o cartão.',
    }[status]
    return toast(toastMessage, {
      type: status === 'success' ? 'neutral' : 'negative',
    })
  }

  const handleOpenCheckoutFallback = (fallbackType: OneClickCheckoutFallbackVariant) => {
    const isEmbeddedFallback = fallbackType === 'embedded-checkout'
    const fallbackUrl = new URL(submitData.submitUrl)
    fallbackUrl.searchParams.set('one_click_failed', 'true')

    const isEmbeddedCheckoutFallback = isEmbeddedFallback && context.componentType === 'modal'

    sendCheckoutFallbackOpenedEvent(isEmbeddedCheckoutFallback)

    const fallbackUrlString = fallbackUrl.toString()
    if (isEmbeddedCheckoutFallback) {
      context.openEmbeddedCheckout(fallbackUrlString, { jwt: submitData.jwt })
    } else {
      window.location.href = fallbackUrlString
    }
  }

  const handleOneClickSuccess = (subscription: Parameters<typeof sendSuccessEvent>[0]) => {
    setStatus('success')
    if (enableSuccessScreen && context.componentType === 'modal') {
      context.setOneClickSuccessData({ ...offerLockCard, viewer })
    } else {
      showToast('success')
    }
    sendSuccessEvent(subscription)
  }

  const handleOneClickFailure = () => {
    setStatus('error')
    sendFailureEvent()

    const isCheckoutFallbackEnabled = isCheckoutFallbackVariant(checkoutFallbackVariant)

    if (isCheckoutFallbackEnabled) {
      handleOpenCheckoutFallback(checkoutFallbackVariant)
    } else {
      showToast('error')
    }
  }

  const onClick = async () => {
    if (!interactive) {
      return
    }

    onClickProp?.()

    setStatus('processing')
    sendSubmittedEvent()

    let orderCode: string | null = null
    let success = false
    try {
      const input = { ccToken, submitData }
      const fetchResult = await oneClickBuy({ variables: { input } })

      const oneClickBuyData = fetchResult.data?.oneClickBuy
      if (oneClickBuyData) {
        ;({ orderCode, success } = oneClickBuyData)
      } else {
        throw new Error('unexpected no data')
      }
    } catch (err) {
      console.error('[shared-box-lock] OneClickBuyMutation', err)
    }

    let result = false
    let currentSubscription = null
    if (success && orderCode) {
      ;({ result, currentSubscription } = await waitSubscriptionUpdate(orderCode))
    }

    if (result) {
      handleOneClickSuccess(currentSubscription)
    } else {
      handleOneClickFailure()
    }
  }

  const cardWithCustomLabel = customLabel
    ? {
        ...offerLockCard,
        button: {
          ...offerLockCard.button,
          label: customLabel,
        },
      }
    : offerLockCard

  return (
    <SubscribeButton
      offerLockCard={cardWithCustomLabel}
      onClick={onClick}
      ref={metricsRef}
      kind={kind}
      leftIcon={leftIcon}
      forceDisabled={forceDisabled}
    />
  )
}

OneClickButton.fragments = {
  offerLockCard: gql`
    fragment OneClickButton_offerLockCard on OfferLockCard {
      offer {
        priceText
        frequency
        presentationName
        frequencyInDays
      }
      creditCard {
        ccToken
      }
      submitData {
        jwt
        submitUrl
      }
      checkoutFallbackVariant

      ...SubscribeButton_offerLockCard
      ...UseSendOneClickButtonEvents_offerLockCard
    }

    ${SubscribeButton.fragments.offerLockCard}
    ${useSendOneClickButtonEvents.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment OneClickButton_viewer on Profile {
      ...useWaitSubscriptionUpdate_viewer
      ...UseSendOneClickButtonEvents_viewer
    }

    ${useWaitSubscriptionUpdate.fragments.viewer}
    ${useSendOneClickButtonEvents.fragments.viewer}
  `,
}
